import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Globe, Loader2, Play, RefreshCw, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { opDeMac } from "@/lib/op-de-mac";

/**
 * CA3 (besluit Frank 1 november 2026): het open bestand als ontwikkelserver draaien en bekijken.
 *
 * Starten en stoppen, de poort, of hij al luistert, de log, en het voorbeeld. Op de Mac laadt het voorbeeld als iframe
 * uit `/api/voorbeeld/<token>/`, met alles wat de pagina zelf ophaalt. Op de telefoon gaat alleen `/api` door de tunnel,
 * en een iframe kan die tunnel niet gebruiken; daar haalt de app de pagina zelf op en toont hem, zonder wat de pagina
 * los ophaalt. In beide gevallen een iframe met `sandbox` zonder `allow-same-origin`: de app in de werkruimte kan niet
 * bij NovaMund zelf.
 */
export function taalVoor(pad) {
  if (pad.endsWith(".js") || pad.endsWith(".mjs")) return "node";
  if (pad.endsWith(".sh")) return "bash";
  return "python";
}

export default function ServerPaneel({ workspaceId, path, voorStart }) {
  const { t } = useTaal();
  const [stand, setStand] = useState({ draait: false, log: "" });
  const [bezig, setBezig] = useState(false);
  const [voorbeeld, setVoorbeeld] = useState(false);
  const [html, setHtml] = useState("");
  const mac = opDeMac();

  const lees = useCallback(() => {
    axios.get(`${API}/workspaces/${workspaceId}/server`).then((r) => setStand(r.data)).catch(() => {});
  }, [workspaceId]);
  useEffect(() => { lees(); }, [lees]);
  // Zolang er een server draait de stand elke drie seconden, voor de log en of hij luistert; daarna niet meer.
  useEffect(() => {
    if (!stand.draait) return undefined;
    const klok = setInterval(lees, 3000);
    return () => clearInterval(klok);
  }, [stand.draait, lees]);

  const laadPagina = useCallback(async (token) => {
    try {
      const { data } = await axios.get(`${API}/voorbeeld/${token}/`, { responseType: "text", transformResponse: (d) => d });
      setHtml(typeof data === "string" ? data : String(data));
    } catch {
      setHtml("");
      toast.error(t("De pagina kwam niet; kijk in de log of de server luistert."));
    }
  }, [t]);

  async function start() {
    if (!path.trim()) return toast.error(t("Open eerst het bestand dat de server is."));
    setBezig(true);
    try {
      await voorStart?.();
      const { data } = await axios.post(`${API}/workspaces/${workspaceId}/server/start`, { path, language: taalVoor(path) });
      setStand(data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("De server kon niet starten."));
    } finally {
      setBezig(false);
    }
  }

  async function stop() {
    setBezig(true);
    try {
      setStand((await axios.post(`${API}/workspaces/${workspaceId}/server/stop`)).data);
      setVoorbeeld(false);
      setHtml("");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? t(detail) : t("Dat lukte niet."));
    } finally {
      setBezig(false);
    }
  }

  function toonVoorbeeld() {
    setVoorbeeld(true);
    if (!mac && stand.token) laadPagina(stand.token);
  }

  return (
    <div className="space-y-2 border-t pt-2" data-testid="server-paneel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="w-full text-xs text-muted-foreground">
          {t("Of draai het open bestand als server en bekijk de app")}{path ? `: ${path}` : ""}
        </span>
        {stand.draait ? (
          <Button variant="outline" className="tap-target" onClick={stop} disabled={bezig} data-testid="server-stop">
            <Square className="h-4 w-4 mr-1" />{t("Stop de server")}
          </Button>
        ) : (
          <Button variant="outline" className="tap-target" onClick={start} disabled={bezig} data-testid="server-start">
            {bezig ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}{t("Start als server")}
          </Button>
        )}
        {stand.draait && (
          <>
            <span className="text-xs" data-testid="server-stand">
              {stand.luistert ? t("Luistert op poort {poort}", { poort: stand.poort }) : t("Start op poort {poort}…", { poort: stand.poort })}
            </span>
            <Button variant="outline" size="sm" className="tap-target" onClick={toonVoorbeeld} disabled={!stand.luistert}
                    data-testid="server-voorbeeld-knop">
              <Globe className="h-4 w-4 mr-1" />{t("Voorbeeld")}
            </Button>
          </>
        )}
        {!stand.draait && stand.exit_code !== undefined && stand.exit_code !== null && (
          <span className="text-xs text-red-600" data-testid="server-gestopt">{t("De server stopte met code {code}.", { code: stand.exit_code })}</span>
        )}
      </div>
      {voorbeeld && stand.draait && stand.token && (
        <div className="space-y-1" data-testid="server-voorbeeld">
          {!mac && (
            <div className="flex items-center gap-2">
              <p className="flex-1 text-xs text-muted-foreground">
                {t("Op de telefoon toont het voorbeeld de pagina zelf; plaatjes, stijlen en scripts die de pagina los ophaalt, komen niet door de verbinding.")}
              </p>
              <Button variant="ghost" size="icon" className="tap-target" onClick={() => laadPagina(stand.token)} aria-label={t("Ververs het voorbeeld")}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          )}
          <iframe title={t("Voorbeeld van de app")} sandbox="allow-scripts allow-forms" data-testid="server-iframe"
                  className="h-80 w-full rounded-md border bg-white"
                  {...(mac ? { src: `${API}/voorbeeld/${stand.token}/` } : { srcDoc: html })} />
        </div>
      )}
      {stand.log && (
        <pre className="max-h-40 overflow-auto rounded-md border bg-muted/30 p-2 text-xs" data-testid="server-log">{stand.log}</pre>
      )}
    </div>
  );
}
